import { ApiError, http, SPATIAL_ROOT, type Fetch } from './http';

/**
 * Everything a shopper has saved, for the account's "My designs" (plan K6).
 *
 * <p>Read from the shopper's account on the server. Kitchen designs are the
 * planner's own saves (`/spatial/kitchen/designs`); door orders and cabinet
 * specs join them once the server stores those kinds too, at which point
 * {@link SavedDesigns.save} accepts them. Nothing is kept in the browser.
 */
export type SavedDesignKind = 'kitchen' | 'bedroom' | 'doors' | 'cabinets' | 'bespoke';

export interface SavedDesign {
  id: string;
  kind: SavedDesignKind;
  title: string;
  /** One line under the title. */
  summary: string;
  /** Where the design opens: the builder or the planner, pre-filled. */
  href: string;
  /** The builder's spec, so the design can be reopened exactly. */
  spec?: unknown;
  /** A small SVG made by the theme's drawing functions. */
  thumbnailSvg?: string;
  /** Total when saved, if the builder had one. */
  total?: number;
  /** A bespoke request's quote status. */
  status?: 'draft' | 'requested' | 'quoted' | 'accepted';
  createdAt: string;
  updatedAt: string;
}

export type NewSavedDesign = Omit<SavedDesign, 'id' | 'createdAt' | 'updatedAt'> & { id?: string };

export interface SavedDesigns {
  list(): Promise<SavedDesign[]>;
  remove(id: string): Promise<void>;
  /** The kinds {@link save} accepts; a builder offers "Save" only for these. */
  readonly saves: ReadonlySet<SavedDesignKind>;
  save(design: NewSavedDesign): Promise<SavedDesign>;
}

/** Thrown when the shopper must sign in first; a page sends them to sign in and back. */
export class SignInRequired extends Error {
  constructor() {
    super('Sign in to see your designs.');
    this.name = 'SignInRequired';
  }
}

interface DesignOverview {
  designs: Array<{ projectId: string; name?: string | null; revision: string; savedAt: string }>;
  limit: number | null;
  used: number;
  trade: boolean;
}

export function accountSavedDesigns(plannerHref: string, fetchImpl?: Fetch): SavedDesigns {
  const api = http(fetchImpl);
  const kitchen = `${SPATIAL_ROOT}/kitchen/designs`;
  const signIn = (e: unknown): never => {
    if (e instanceof ApiError && (e.status === 401 || e.status === 403)) throw new SignInRequired();
    throw e;
  };
  return {
    saves: new Set<SavedDesignKind>(),
    async list() {
      const overview = await api.get<DesignOverview>(`${kitchen}/overview`).catch(signIn);
      return overview.designs.map((d) => ({
        id: d.projectId,
        kind: 'kitchen' as const,
        title: d.name?.trim() || 'Untitled kitchen',
        summary: 'Kitchen design',
        href: `${plannerHref}${plannerHref.includes('?') ? '&' : '?'}project=${encodeURIComponent(d.projectId)}`,
        createdAt: d.savedAt,
        updatedAt: d.savedAt,
      }));
    },
    async remove(id) {
      await api.send('DELETE', `${kitchen}/${encodeURIComponent(id)}`).catch(signIn);
    },
    async save() {
      throw new Error('This kind of design cannot be saved to your account yet.');
    },
  };
}
